import { useEffect, useRef, useState } from "react";
import { Loader2, Save, Plus, Trash2, Download, Upload, DatabaseBackup } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { inputCls, inputFitCls, Field, Card, downloadFile, todayISO } from "./booksShared";

export const BooksSettings = () => {
  const [settings, setSettings] = useState(null);
  const [busy, setBusy] = useState(false);
  const [newCategory, setNewCategory] = useState("");
  const [newCity, setNewCity] = useState("");
  const [newCityRate, setNewCityRate] = useState("");
  const [credForm, setCredForm] = useState({ current_password: "", new_email: "", new_password: "" });
  const [credBusy, setCredBusy] = useState(false);
  const [promos, setPromos] = useState([]);
  const [promoForm, setPromoForm] = useState({ code: "", type: "percent", value: "", buy_x: 2, get_y: 1 });

  const loadPromos = () => api.get("/admin/promos").then((r) => setPromos(r.data)).catch(() => toast.error("Could not load promo codes"));

  const promoLabel = (p) =>
    p.type === "percent" ? `${p.value}% off` : p.type === "free_delivery" ? "Free delivery" : `Buy ${p.buy_x} get ${p.get_y} free`;

  const addPromo = async () => {
    try {
      await api.post("/admin/promos", {
        code: promoForm.code,
        type: promoForm.type,
        value: parseFloat(promoForm.value) || 0,
        buy_x: parseInt(promoForm.buy_x) || 0,
        get_y: parseInt(promoForm.get_y) || 0,
      });
      toast.success("Promo code created");
      setPromoForm({ code: "", type: "percent", value: "", buy_x: 2, get_y: 1 });
      await loadPromos();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const togglePromo = async (p) => {
    try {
      await api.put(`/admin/promos/${p.id}`, { code: p.code, type: p.type, value: p.value, buy_x: p.buy_x, get_y: p.get_y, active: !p.active });
      await loadPromos();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const removePromo = async (id) => {
    try {
      await api.delete(`/admin/promos/${id}`);
      toast.success("Promo deleted");
      await loadPromos();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const changeCredentials = async () => {
    if (!credForm.current_password || !credForm.new_email || !credForm.new_password) {
      toast.error("Fill in all three sign-in fields.");
      return;
    }
    setCredBusy(true);
    try {
      const { data } = await api.post("/auth/change-credentials", credForm);
      localStorage.setItem("pmok_admin_token", data.token);
      setCredForm({ current_password: "", new_email: "", new_password: "" });
      toast.success(`Sign-in updated — use ${data.email} and your new password next time`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setCredBusy(false);
    }
  };
  const importRef = useRef(null);

  useEffect(() => {
    api.get("/admin/books/settings").then((r) => setSettings(r.data)).catch(() => toast.error("Could not load settings"));
    loadPromos();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const set = (k) => (e) => setSettings((s) => ({ ...s, [k]: e.target.value }));
  const setRate = (k) => (e) =>
    setSettings((s) => ({ ...s, tax_rates: { ...s.tax_rates, [k]: parseFloat(e.target.value) || 0 } }));

  const addCategory = () => {
    const c = newCategory.trim();
    if (!c) return;
    setSettings((s) => ({ ...s, expense_categories: [...(s.expense_categories || []), c] }));
    setNewCategory("");
  };

  const removeCategory = (c) =>
    setSettings((s) => ({ ...s, expense_categories: (s.expense_categories || []).filter((x) => x !== c) }));

  const setCityRate = (i, k) => (e) =>
    setSettings((s) => ({
      ...s,
      city_rates: (s.city_rates || []).map((c, idx) =>
        idx === i ? { ...c, [k]: k === "rate" ? parseFloat(e.target.value) || 0 : e.target.value } : c
      ),
    }));

  const addCityRate = () => {
    const city = newCity.trim();
    if (!city) return;
    setSettings((s) => ({
      ...s,
      city_rates: [...(s.city_rates || []), { city, rate: parseFloat(newCityRate) || 0 }],
    }));
    setNewCity("");
    setNewCityRate("");
  };

  const removeCityRate = (i) =>
    setSettings((s) => ({ ...s, city_rates: (s.city_rates || []).filter((_, idx) => idx !== i) }));

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.put("/admin/books/settings", settings);
      setSettings(data);
      toast.success("Settings saved");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const exportData = async (backup = false) => {
    try {
      const { data } = await api.get("/admin/books/export");
      const name = backup ? `picklemeok-backup-${todayISO()}.json` : `picklemeok-data-${todayISO()}.json`;
      downloadFile(name, JSON.stringify(data, null, 2));
      toast.success(backup ? "Backup downloaded" : "Data exported");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const importData = async (file) => {
    if (!file) return;
    try {
      const parsed = JSON.parse(await file.text());
      const { data } = await api.post("/admin/books/import", {
        sales: parsed.sales || [],
        expenses: parsed.expenses || [],
        settings: parsed.settings || {},
      });
      toast.success(`Imported ${data.imported} record${data.imported === 1 ? "" : "s"}`);
      const { data: fresh } = await api.get("/admin/books/settings");
      setSettings(fresh);
    } catch {
      toast.error("That file couldn't be read — use a file made by Export/Backup.");
    }
  };

  if (!settings) {
    return <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-dill" /></div>;
  }

  return (
    <div className="space-y-5" data-testid="books-settings">
      <Card title="Business Information" testid="settings-business">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="Business Name"><input value={settings.business_name || ""} onChange={set("business_name")} className={inputCls} data-testid="settings-business-name" /></Field>
          <Field label="Business Address"><input value={settings.business_address || ""} onChange={set("business_address")} className={inputCls} data-testid="settings-business-address" /></Field>
          <Field label="Default City"><input value={settings.default_city || ""} onChange={set("default_city")} className={inputCls} data-testid="settings-default-city" /></Field>
          <Field label="Default County"><input value={settings.default_county || ""} onChange={set("default_county")} className={inputCls} data-testid="settings-default-county" /></Field>
        </div>
      </Card>

      <Card title="Tax Settings" desc="Your federal rate is used for the year-end set-aside estimate." testid="settings-tax">
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
          <Field label="Federal Rate %"><input type="number" step="0.01" min="0" value={settings.tax_rates?.federal ?? 0} onChange={setRate("federal")} className={inputCls} data-testid="settings-rate-federal" /></Field>
          <Field label="State Tax Due Day"><input type="number" min="1" max="28" value={settings.state_due_day ?? 21} onChange={(e) => setSettings((s) => ({ ...s, state_due_day: parseInt(e.target.value) || 21 }))} className={inputCls} data-testid="settings-due-day" /></Field>
        </div>
        <p className="text-[11px] text-ink/40">Sales tax you collect each month is due to the state on this day of the following month (yours is the 21st).</p>
        <div>
          <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-ink/50 mb-2">City Tax Rates (charged to customers at checkout)</p>
          <div className="space-y-2">
            {(settings.city_rates || []).map((c, i) => (
              <div key={i} className="flex items-center gap-3" data-testid={`city-rate-row-${i}`}>
                <input value={c.city} onChange={setCityRate(i, "city")} placeholder="City" className={`${inputFitCls} flex-1 min-w-0`} data-testid={`city-rate-name-${i}`} />
                <input type="number" step="0.01" min="0" value={c.rate} onChange={setCityRate(i, "rate")} className={`${inputFitCls} w-28 shrink-0`} data-testid={`city-rate-pct-${i}`} />
                <button onClick={() => removeCityRate(i)} className="p-2 text-ink/35 hover:text-terra transition-colors" aria-label="Remove city" data-testid={`city-rate-remove-${i}`}>
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
          <div className="flex flex-wrap gap-2 mt-3">
            <input value={newCity} onChange={(e) => setNewCity(e.target.value)} placeholder="New city…" className={`${inputFitCls} flex-1 min-w-40`} data-testid="settings-new-city" />
            <input type="number" step="0.01" min="0" value={newCityRate} onChange={(e) => setNewCityRate(e.target.value)} placeholder="Rate %" className={`${inputFitCls} w-28`} data-testid="settings-new-city-rate" />
            <button onClick={addCityRate} className="flex items-center gap-1.5 border border-parchborder hover:border-dill text-ink/60 rounded-xl px-4 py-2.5 text-sm transition-colors" data-testid="settings-add-city">
              <Plus className="w-4 h-4" /> Add New
            </button>
          </div>
          <p className="text-[11px] text-ink/40 mt-2">Sales recorded with a matching city use that city's rate in the estimate.</p>
        </div>
      </Card>

      <Card title="Expense Categories" desc="Used in the Expenses tab dropdown." testid="settings-categories">
        <div className="flex flex-wrap gap-2">
          {(settings.expense_categories || []).map((c) => (
            <span key={c} className="flex items-center gap-2 bg-parchdeep rounded-full px-4 py-2 text-sm text-ink/75" data-testid={`category-chip-${c}`}>
              {c}
              <button onClick={() => removeCategory(c)} className="text-ink/35 hover:text-terra transition-colors" aria-label={`Remove ${c}`} data-testid={`category-remove-${c}`}>
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </span>
          ))}
        </div>
        <div className="flex gap-2">
          <input value={newCategory} onChange={(e) => setNewCategory(e.target.value)} placeholder="New category…" className={`${inputCls} max-w-xs`} data-testid="settings-new-category" />
          <button onClick={addCategory} className="flex items-center gap-1.5 border border-parchborder hover:border-dill text-ink/60 rounded-xl px-4 py-2.5 text-sm transition-colors" data-testid="settings-add-category">
            <Plus className="w-4 h-4" /> Add
          </button>
        </div>
      </Card>

      <Card title="Promo Codes" desc="Customers enter these in their basket — the discount applies at checkout automatically." testid="settings-promos">
        {promos.length === 0 && <p className="text-sm text-ink/50 italic">No promo codes yet — add your first below.</p>}
        <div className="space-y-2">
          {promos.map((p) => (
            <div key={p.id} className="flex flex-wrap items-center justify-between gap-3 bg-parchdeep/50 rounded-xl px-4 py-3" data-testid={`promo-row-${p.code}`}>
              <div>
                <span className="font-mono font-bold text-sm text-dill">{p.code}</span>
                <span className="text-xs text-ink/55 ml-2">{promoLabel(p)}</span>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => togglePromo(p)}
                  className={`font-mono text-[10px] uppercase tracking-widest px-3 py-1.5 rounded-full transition-colors ${
                    p.active ? "bg-dill/10 text-dill" : "bg-ink/5 text-ink/40"
                  }`}
                  data-testid={`promo-toggle-${p.code}`}
                >
                  {p.active ? "Active" : "Off"}
                </button>
                <button onClick={() => removePromo(p.id)} className="p-2 text-ink/35 hover:text-terra transition-colors" aria-label={`Delete ${p.code}`} data-testid={`promo-delete-${p.code}`}>
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
        <div className="flex flex-wrap items-end gap-3 border-t border-parchborder pt-4">
          <Field label="Code">
            <input value={promoForm.code} onChange={(e) => setPromoForm((f) => ({ ...f, code: e.target.value.toUpperCase() }))} placeholder="PICKLE10" className={`${inputFitCls} w-36 uppercase`} data-testid="promo-new-code" />
          </Field>
          <Field label="Type">
            <select value={promoForm.type} onChange={(e) => setPromoForm((f) => ({ ...f, type: e.target.value }))} className={`${inputFitCls} w-auto`} data-testid="promo-new-type">
              <option value="percent">% off order</option>
              <option value="free_delivery">Free delivery</option>
              <option value="bxgy">Buy X get Y free</option>
            </select>
          </Field>
          {promoForm.type === "percent" && (
            <Field label="Percent %">
              <input type="number" step="1" min="1" max="100" value={promoForm.value} onChange={(e) => setPromoForm((f) => ({ ...f, value: e.target.value }))} className={`${inputFitCls} w-24`} data-testid="promo-new-value" />
            </Field>
          )}
          {promoForm.type === "bxgy" && (
            <>
              <Field label="Buy">
                <input type="number" min="1" value={promoForm.buy_x} onChange={(e) => setPromoForm((f) => ({ ...f, buy_x: e.target.value }))} className={`${inputFitCls} w-20`} data-testid="promo-new-buy" />
              </Field>
              <Field label="Get free">
                <input type="number" min="1" value={promoForm.get_y} onChange={(e) => setPromoForm((f) => ({ ...f, get_y: e.target.value }))} className={`${inputFitCls} w-20`} data-testid="promo-new-get" />
              </Field>
            </>
          )}
          <button onClick={addPromo} className="flex items-center gap-1.5 bg-dill hover:bg-terra text-parchment rounded-xl px-4 py-2.5 text-sm font-semibold transition-colors" data-testid="promo-add-button">
            <Plus className="w-4 h-4" /> Add Promo
          </button>
        </div>
      </Card>

      <Card title="Data" desc="Export for your accountant, or keep a backup." testid="settings-data">
        <div className="flex flex-wrap gap-3">
          <button onClick={() => exportData(false)} className="flex items-center gap-2 border border-parchborder hover:border-terra hover:text-terra text-ink/60 font-semibold rounded-full px-5 py-2.5 text-sm transition-colors" data-testid="settings-export">
            <Download className="w-4 h-4" /> Export Data
          </button>
          <button onClick={() => importRef.current?.click()} className="flex items-center gap-2 border border-parchborder hover:border-terra hover:text-terra text-ink/60 font-semibold rounded-full px-5 py-2.5 text-sm transition-colors" data-testid="settings-import">
            <Upload className="w-4 h-4" /> Import Data
          </button>
          <button onClick={() => exportData(true)} className="flex items-center gap-2 border border-parchborder hover:border-terra hover:text-terra text-ink/60 font-semibold rounded-full px-5 py-2.5 text-sm transition-colors" data-testid="settings-backup">
            <DatabaseBackup className="w-4 h-4" /> Backup Data
          </button>
          <input ref={importRef} type="file" accept="application/json" className="hidden" onChange={(e) => importData(e.target.files?.[0])} data-testid="settings-import-file" />
        </div>
      </Card>

      <Card title="Sign-in Details" desc="Change the email and password for this Pantry Portal." testid="settings-signin">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <Field label="Current password">
            <input type="password" value={credForm.current_password} onChange={(e) => setCredForm((f) => ({ ...f, current_password: e.target.value }))} className={inputCls} data-testid="signin-current-password" />
          </Field>
          <Field label="New email">
            <input type="email" value={credForm.new_email} onChange={(e) => setCredForm((f) => ({ ...f, new_email: e.target.value }))} placeholder="admin@picklemeok.com" className={inputCls} data-testid="signin-new-email" />
          </Field>
          <Field label="New password (8+ characters)">
            <input type="password" value={credForm.new_password} onChange={(e) => setCredForm((f) => ({ ...f, new_password: e.target.value }))} className={inputCls} data-testid="signin-new-password" />
          </Field>
        </div>
        <button
          onClick={changeCredentials}
          disabled={credBusy}
          className="bg-dill hover:bg-terra disabled:opacity-60 text-parchment font-bold rounded-full px-6 py-2.5 text-sm transition-colors flex items-center gap-2"
          data-testid="signin-save-button"
        >
          {credBusy && <Loader2 className="w-4 h-4 animate-spin" />} Update Sign-in
        </button>
      </Card>

      <button
        onClick={save}
        disabled={busy}
        className="bg-dill hover:bg-terra disabled:opacity-60 text-parchment font-bold rounded-full px-8 py-3 text-sm uppercase tracking-widest transition-colors flex items-center gap-2"
        data-testid="settings-save-button"
      >
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
        Save Settings
      </button>
    </div>
  );
};
